import { Fragment, useEffect } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { Layer } from "@/Utils/hierarchy";

import { useBrandEquity } from "./api/getBrandEquity";
import BrandEquityChart from "./components/Charts/BrandEquityChart";
import { content } from "./constants/content";
import { useAvailableFilters } from "./hooks/useAvailableFilters";
import { useBrandEquityStore } from "./store/useBrandEquityStore";

interface BrandEquityProps {}

export const BrandEquity: React.FC<BrandEquityProps> = () => {
  const { nodes, loading } = useInfo();
  const { startDate, endDate } = useDateRangeStore();

  const { targetNodeUUID } = useBrandEquityStore((state) => state.filters);
  const { setTargetNode, chart } = useBrandEquityStore();

  const availableFilters = useAvailableFilters();

  // Set default node
  useEffect(() => {
    if (nodes && !loading && targetNodeUUID === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;

      if (defaultNodeUUID !== undefined) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNodeUUID, nodes]);

  // Force hash write on mount if we have a targetNode
  // This ensures URL persistence when navigating between pages
  useEffect(() => {
    if (targetNodeUUID) {
      setTargetNode(targetNodeUUID);
    }
  }, []);

  const selectedNode = nodes?.getNode(targetNodeUUID || "");

  const { moduleOpened } = useAnalytics();

  const {
    data: brandEquityData,
    isLoading: brandEquityLoading,
    error,
  } = useBrandEquity({
    brand: selectedNode?.node.brand || "",
    category: selectedNode?.node.category || "",
    product: selectedNode?.node.product || "",
    startDate,
    endDate,
    dateGranularity: chart.dateGranularity,
    config: {
      enabled: !!selectedNode && !!startDate && !!endDate,
    },
  });

  useEffect(() => {
    moduleOpened("brand-equity", Layer.Portfolio);
  }, []);

  if (!nodes || loading || !targetNodeUUID) {
    return <CircularProgress size={150} sx={{ marginY: "30rem" }} />;
  }

  return (
    <Box>
      <Box marginBottom={3}>
        <Title title={content.title + " v2"} subtitle={content.subtitle} />
        <Header availableFilters={availableFilters} />
        {brandEquityLoading || !selectedNode ? (
          <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
            <CircularProgress size={150} />
          </Stack>
        ) : (
          <Fragment>
            <BrandEquityChart
              monthAllResponse={brandEquityData as any}
              monthErrors={error as any}
              weekAllResponse={brandEquityData as any}
              weekErrors={error as any}
            />
          </Fragment>
        )}
      </Box>
    </Box>
  );
};

export default BrandEquity;
